import { useInfiniteQuery } from 'react-query';
import { EisRulesResponse, EisRulesListParams } from '../_types';
import { performanceReportingApiUrl, fetcher, handleError, ServerErrorResponse, createParamString } from 'data_sources/constants';

// Fetch EIS rules from API
export const getEisRules = async (
  params: Record<string, any> = {},
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<EisRulesResponse> => {
  try {
    const url = `${performanceReportingApiUrl()}/performance/eis/v1/rules`;

    // Build query parameters
    const queryParams = createParamString(params);
    const fullUrl = queryParams ? `${url}?${queryParams}` : url;

    const response = await fetcher({
      url: fullUrl,
      method: 'GET',
      options: { signal },
    });

    if (response.errorDetails) {
      if (onError) onError(response);
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    } else if (onError && error instanceof Error) {
      handleError(error, { onError });
    }
    throw error;
  }
};

export const useEisRules = (
  params: EisRulesListParams = {},
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) => {
  return useInfiniteQuery<EisRulesResponse, ServerErrorResponse>(
    ['eisRules', JSON.stringify(params)],
    async ({ pageParam, signal }) => {
      const combinedParams = {
        ...params,
        nextToken: pageParam,
      };
      return await getEisRules(combinedParams, signal, onError);
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
      ...options,
    }
  );
};

